import axios, { AxiosError, AxiosRequestConfig } from "axios";
import useSWR, { SWRConfiguration } from "swr";

import { logApiError, logApiRequest, logApiResponse } from "../../common/helpers/apiLogger";
import generalRequestErrorHandler from "./generalRequestErrorHandler";

interface IApiRequestGetConfig<TResponse> {
	url: string | null;
	axiosConfig?: AxiosRequestConfig;
	swrConfig?: SWRConfiguration<TResponse, AxiosError>;
	onError?: (error: AxiosError | Error) => void;
	triggerOnMount?: boolean;
}

interface IApiRequestGetResult<TResponse> {
	data: TResponse | undefined;
	isLoading: boolean;
	isValidating: boolean;
	error: AxiosError | undefined;
	getUpdateData: ReturnType<typeof useSWR<TResponse, AxiosError>>["mutate"];
}

const fetcher = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
	const startTime = Date.now();

	// Log the request
	logApiRequest({
		url,
		method: "GET",
		headers: config?.headers,
	});

	try {
		// Artificial delay for testing
		// await new Promise((resolve) => setTimeout(resolve, 2000));
		const response = await axios.get<T>(url, config);
		const duration = Date.now() - startTime;

		// Log successful response
		logApiResponse({
			url,
			method: "GET",
			status: response.status,
			responseData: response.data,
			duration,
		});

		return response.data;
	} catch (error: any) {
		const duration = Date.now() - startTime;

		// Log error response
		logApiError({
			url,
			method: "GET",
			status: error.response?.status,
			error: error.response?.data || error.message,
			duration,
		});

		generalRequestErrorHandler(error as AxiosError);
		throw error;
	}
};

const useApiRequestGet = <TResponse = any>(
	config: IApiRequestGetConfig<TResponse>
): IApiRequestGetResult<TResponse> => {
	const { triggerOnMount = true, ...restConfig } = config;

	const { data, error, isLoading, mutate, isValidating } = useSWR<TResponse, AxiosError>(
		restConfig.url,
		triggerOnMount ? (url) => fetcher<TResponse>(url, restConfig.axiosConfig) : null,
		{
			...restConfig.swrConfig,
			revalidateOnMount: triggerOnMount,
			onError: (err) => {
				if (restConfig.onError) {
					restConfig.onError(err);
				}
			},
		}
	);

	return {
		data,
		isLoading,
		isValidating,
		error,
		getUpdateData: mutate,
	};
};

export default useApiRequestGet;
